import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fetchTabularData, exportToCSV, fetchAllTabularDataForCSV } from '../../api/reportService';
import { fetchPatients } from '../../api/patientService';

const TabularData = () => {
  // Add custom styles for date picker to match native design
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .react-datepicker-popper {
        z-index: 9999 !important;
      }

      .react-datepicker {
        font-family: inherit;
        border: 1px solid #ddd;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        font-size: 0.85rem;
      }

      .react-datepicker__header {
        background-color: white;
        border-bottom: none;
        padding: 10px 8px 6px;
      }

      .react-datepicker__current-month {
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 8px;
      }

      .react-datepicker__header__dropdown {
        display: flex;
        justify-content: center;
        gap: 6px;
        margin-bottom: 8px;
      }

      .react-datepicker__month-dropdown-container,
      .react-datepicker__year-dropdown-container {
        margin: 0;
      }

      .react-datepicker__month-read-view,
      .react-datepicker__year-read-view {
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 4px 20px 4px 8px;
        background: white;
        position: relative;
        font-size: 13px;
      }

      .react-datepicker__month-read-view--down-arrow,
      .react-datepicker__year-read-view--down-arrow {
        top: 8px;
        right: 6px;
        border-width: 0.25rem;
      }

      .react-datepicker__navigation {
        top: 14px;
        width: 24px;
        height: 24px;
      }

      .react-datepicker__navigation--previous {
        left: 8px;
      }

      .react-datepicker__navigation--next {
        right: 8px;
      }

      .react-datepicker__day-names {
        margin-top: 6px;
      }

      .react-datepicker__day-name,
      .react-datepicker__day {
        width: 1.9rem;
        line-height: 1.9rem;
        margin: 0.15rem;
        font-size: 13px;
      }

      .react-datepicker__day:hover {
        border-radius: 4px;
        background-color: #f0f0f0;
      }

      .react-datepicker__day--selected,
      .react-datepicker__day--keyboard-selected {
        border-radius: 4px;
        background-color: #1976d2;
        color: white;
        font-weight: 500;
      }

      .react-datepicker__day--today {
        font-weight: 600;
      }

      .react-datepicker__today-button {
        background: white;
        border-top: 1px solid #eee;
        padding: 8px 12px;
        text-align: right;
        color: #1976d2;
        font-weight: 500;
        cursor: pointer;
        font-size: 13px;
      }

      .react-datepicker__today-button:hover {
        background-color: #f8f8f8;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [patients, setPatients] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    total: 0,
  });

  // Sample hardcoded data as fallback (comment out or remove after testing)
  const [sampleData] = useState([
    {
      id: 1,
      eventDate: '2024-02-25',
      eventType: 'Acute Attack',
      attackDuration: '4 hours',
      medicationHAE: 'Berinert',
      medicationHAEDose: '500 IU',
      otherMedication: 'None',
      treatmentLocation: 'Home',
      medicationInjectionSite: 'Abdomen',
      attackSeverity: 'Moderate',
      missedDays: 1,
      areaAffected: 'Facial',
      triggers: 'Stress',
      timeToTreatment: '30 minutes',
      signsAndSymptoms: 'Swelling, Pain',
      patientGender: 'Female',
      patientAge: 28,
      typeOfHAE: 'Type I',
      dateOfDiagnosis: '2020-05-15',
      physicianName: 'Dr. Sarah Johnson',
      patientId: 'P12345',
      physicianEmail: 'sarah.johnson@medical.com'
    },
    {
      id: 2,
      eventDate: '2024-02-23',
      eventType: 'Acute Attack',
      attackDuration: '6 hours',
      medicationHAE: 'Haegarda',
      medicationHAEDose: '3000 IU',
      otherMedication: 'Painkillers',
      treatmentLocation: 'Hospital',
      medicationInjectionSite: 'Upper Arm',
      attackSeverity: 'Severe',
      missedDays: 2,
      areaAffected: 'Abdominal',
      triggers: 'Physical Trauma',
      timeToTreatment: '45 minutes',
      signsAndSymptoms: 'Severe Pain, Nausea, Vomiting',
      patientGender: 'Male',
      patientAge: 35,
      typeOfHAE: 'Type II',
      dateOfDiagnosis: '2019-08-20',
      physicianName: 'Dr. Michael Chen',
      patientId: 'P12346',
      physicianEmail: 'michael.chen@medical.com'
    },
    {
      id: 3,
      eventDate: '2024-02-20',
      eventType: 'Prophylaxis',
      attackDuration: 'N/A',
      medicationHAE: 'Cinryze',
      medicationHAEDose: '1000 IU',
      otherMedication: 'None',
      treatmentLocation: 'Clinic',
      medicationInjectionSite: 'Thigh',
      attackSeverity: 'N/A',
      missedDays: 0,
      areaAffected: 'N/A',
      triggers: 'Scheduled Dose',
      timeToTreatment: 'N/A',
      signsAndSymptoms: 'None',
      patientGender: 'Female',
      patientAge: 42,
      typeOfHAE: 'Type I',
      dateOfDiagnosis: '2018-03-10',
      physicianName: 'Dr. Emily Wilson',
      patientId: 'P12347',
      physicianEmail: 'emily.wilson@medical.com'
    },
    {
      id: 4,
      eventDate: '2024-02-18',
      eventType: 'Acute Attack',
      attackDuration: '3 hours',
      medicationHAE: 'Berinert',
      medicationHAEDose: '750 IU',
      otherMedication: 'Antiemetics',
      treatmentLocation: 'Emergency Room',
      medicationInjectionSite: 'Abdomen',
      attackSeverity: 'Severe',
      missedDays: 3,
      areaAffected: 'Laryngeal',
      triggers: 'Unknown',
      timeToTreatment: '15 minutes',
      signsAndSymptoms: 'Difficulty Breathing, Throat Swelling',
      patientGender: 'Male',
      patientAge: 50,
      typeOfHAE: 'Type I',
      dateOfDiagnosis: '2015-11-25',
      physicianName: 'Dr. Robert Martinez',
      patientId: 'P12348',
      physicianEmail: 'robert.martinez@medical.com'
    },
    {
      id: 5,
      eventDate: '2024-02-15',
      eventType: 'Acute Attack',
      attackDuration: '5 hours',
      medicationHAE: 'Haegarda',
      medicationHAEDose: '2000 IU',
      otherMedication: 'None',
      treatmentLocation: 'Home',
      medicationInjectionSite: 'Upper Arm',
      attackSeverity: 'Mild',
      missedDays: 0,
      areaAffected: 'Peripheral',
      triggers: 'Dental Procedure',
      timeToTreatment: '20 minutes',
      signsAndSymptoms: 'Mild Swelling, Discomfort',
      patientGender: 'Female',
      patientAge: 31,
      typeOfHAE: 'Type II',
      dateOfDiagnosis: '2021-01-05',
      physicianName: 'Dr. Sarah Johnson',
      patientId: 'P12349',
      physicianEmail: 'sarah.johnson@medical.com'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');

  // Calculate default date range (1 month from today)
  const getDefaultDateRange = () => {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);

    return {
      start: oneMonthAgo.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  };

  const defaultDates = getDefaultDateRange();

  const [filters, setFilters] = useState({
    startDate: defaultDates.start,
    endDate: defaultDates.end,
    haeType: 'all',
    gender: 'all',
    ageRange: 'all',
    selectedPatients: []
  });
  const [showFilters, setShowFilters] = useState(true);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  // Helper function to remove trailing commas and trim whitespace
  const cleanText = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text.replace(/,\s*$/, '').trim();
  };

  // Helper function to convert text to Title Case
  const toTitleCase = (text) => {
    if (!text || typeof text !== 'string') return text;
    if (text.toUpperCase() === 'N/A') return 'N/A';

    // Handle special cases
    const specialCases = {
      'hae': 'HAE',
      'iu': 'IU',
      'n/a': 'N/A'
    };

    return text
      .replace(/,\s*/g, ', ')
      .toLowerCase()
      .split(' ')
      .map(word => {
        // Check for special cases
        const lowerWord = word.toLowerCase();
        if (specialCases[lowerWord]) {
          return specialCases[lowerWord];
        }
        // Capitalize first letter
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  // Helper function to convert HAE type to Roman numerals
  const toRomanNumeral = (haeType) => {
    if (!haeType) return '';
    const type = haeType.toString().toLowerCase().replace(/type\s*/i, '').trim();
    const romanMap = {
      '1': 'I', 'i': 'I',
      '2': 'II', 'ii': 'II',
      '3': 'III', 'iii': 'III'
    };
    return romanMap[type] || type.toUpperCase();
  };

  // Load tabular data from API with server-side pagination
  const loadTabularData = async () => {
    // If one date is filled but not the other, don't call API (show warning)
    const hasPartialDates = (filters.startDate && !filters.endDate) || (!filters.startDate && filters.endDate);

    if (hasPartialDates) {
      console.log('[TabularData] Skipping API call - both dates must be filled or both empty');
      setData([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      setLoading(false);
      return;
    }

    // If both dates are empty OR both are filled, proceed with API call
    try {
      setLoading(true);
      setError(null);

      const requestParams = {
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        hae: filters.haeType,
        gender: filters.gender,
        age: filters.ageRange,
        limit: pagination.pageSize,
        skip: (pagination.currentPage - 1) * pagination.pageSize,
        patientIds: filters.selectedPatients.length > 0 ? filters.selectedPatients : undefined,
      };

      console.log('[TabularData] ===== API REQUEST =====');
      console.log('[TabularData] Request params:', requestParams);
      console.log('[TabularData] Filters:', filters);
      console.log('[TabularData] Pagination:', pagination);

      const result = await fetchTabularData(requestParams);

      console.log('[TabularData] ===== API RESPONSE =====');
      console.log('[TabularData] Total records in DB:', result.total);
      console.log('[TabularData] Records returned:', result.data.length);
      console.log('[TabularData] First event sample:', result.data[0]);

      // DEBUG: Count events by type
      const typeCount = {};
      result.data.forEach(event => {
        const type = event.type || 'unknown';
        typeCount[type] = (typeCount[type] || 0) + 1;
      });
      console.log('[TabularData] Events by type received from API:', typeCount);
      console.log('[TabularData] ========================');

      // Medications to exclude from display
      const excludedMedications = ['cinryze'];

      // Transform API data to match component's expected format
      const transformedData = result.data.map((event, index) => {
        if (index === 0) {
          console.log('[TabularData] Transforming first event:', event);
          console.log('[TabularData] Patient data:', event.patient);
          console.log('[TabularData] Patient otherDetail:', event.patient?.otherDetail);
        }
        // Safely transform vials array to strings (filter out excluded medications like Cinryze)
        let medicationHAE = 'N/A';
        let medicationHAEDose = 'N/A';

        if (event.vials && Array.isArray(event.vials) && event.vials.length > 0) {
          try {
            // Filter out excluded medications (Cinryze)
            const filteredVials = event.vials.filter(v =>
              !excludedMedications.includes(v?.name?.toLowerCase())
            );
            if (filteredVials.length > 0) {
              medicationHAE = cleanText(filteredVials.map(v => v?.name || 'Unknown').join(', '));
              medicationHAEDose = cleanText(filteredVials.map(v => `${v?.dose || 0} ${(v?.doseType || '').toLowerCase()}`).join(', '));
            }
          } catch (err) {
            console.error('Error transforming vials:', err, event.vials);
          }
        }

        // Handle otherMedications - could be array or string
        let otherMedication = 'N/A';
        if (event.otherMedications) {
          if (Array.isArray(event.otherMedications) && event.otherMedications.length > 0) {
            const medications = event.otherMedications
              .filter(m => m && (m.name || (typeof m === 'string' && m.trim())))
              .map(m => {
                if (typeof m === 'string') {
                  return m.trim();
                }
                // Format: "Medication (dose doseType)"
                const name = m.name || 'Unknown';
                const dose = m.dose ? ` (${m.dose}${m.doseType ? ' ' + m.doseType : ''})` : '';
                return name + dose;
              });
            otherMedication = medications.length > 0 ? cleanText(medications.join(', ')) : 'N/A';
          } else if (typeof event.otherMedications === 'string' && event.otherMedications.trim()) {
            otherMedication = event.otherMedications.trim();
          }
        }

        return {
        id: event._id,
        eventDate: event.date || 'N/A',
        eventTime: event.time || 'N/A',
        eventType: toTitleCase(event.type) || 'N/A',
        attackDuration: event.duration ? (() => {
          // Convert seconds to "x Days xxh:xxm:xxs" format
          const days = Math.floor(event.duration / 86400);
          const hours = Math.floor((event.duration % 86400) / 3600);
          const minutes = Math.floor((event.duration % 3600) / 60);
          const seconds = event.duration % 60;

          const paddedHours = hours.toString().padStart(2, '0');
          const paddedMinutes = minutes.toString().padStart(2, '0');
          const paddedSeconds = seconds.toString().padStart(2, '0');

          return `${days} Days ${paddedHours}h:${paddedMinutes}m:${paddedSeconds}s`;
        })() : 'N/A',
        medicationHAE: toTitleCase(medicationHAE) || 'N/A',
        medicationHAEDose: medicationHAEDose || 'N/A',
        otherMedication: toTitleCase(otherMedication) || 'N/A',
        treatmentLocation: toTitleCase(event.treatmentLocation) || 'N/A',
        medicationInjectionSite: toTitleCase(cleanText(event.medication_injection_site)) || 'N/A',
        attackSeverity: (() => {
          const eventType = (event.type || '').toLowerCase();
          if (eventType === 'prodrome' || eventType === 'prophylaxis') return 'N/A';
          if (event.severity === null || event.severity === undefined) return 'N/A';
          return `${event.severity}/10`;
        })(),
        missedDays: (() => {
          // Default to 0 for attack events
          const eventType = (event.type || '').toLowerCase();
          if (eventType === 'attack') {
            return event.daysOffWork !== null && event.daysOffWork !== undefined ? event.daysOffWork : 0;
          }
          return event.daysOffWork !== null && event.daysOffWork !== undefined ? event.daysOffWork : 'N/A';
        })(),
        areaAffected: (() => {
          if (Array.isArray(event.areasAffected) && event.areasAffected.length > 0) {
            const joined = cleanText(event.areasAffected.filter(a => a && a.trim()).join(', '));
            return toTitleCase(joined) || 'N/A';
          }
          return (event.areasAffected && event.areasAffected.trim && event.areasAffected.trim() !== '') ? toTitleCase(cleanText(event.areasAffected)) : 'N/A';
        })(),
        triggers: (() => {
          if (Array.isArray(event.triggers) && event.triggers.length > 0) {
            const joined = cleanText(event.triggers.filter(t => t && t.trim()).join(', '));
            return toTitleCase(joined) || 'N/A';
          }
          return (event.triggers && event.triggers.trim && event.triggers.trim() !== '') ? toTitleCase(cleanText(event.triggers)) : 'N/A';
        })(),
        timeToTreatment: (event.timeBetween && parseFloat(event.timeBetween) > 0) ? (() => {
          const totalSeconds = parseFloat(event.timeBetween);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          const paddedHours = hours.toString().padStart(2, '0');
          const paddedMinutes = minutes.toString().padStart(2, '0');
          const paddedSeconds = seconds.toString().padStart(2, '0');
          return `${paddedHours}h:${paddedMinutes}m:${paddedSeconds}s`;
        })() : 'N/A',
        signsAndSymptoms: (() => {
          if (Array.isArray(event.signSymptoms) && event.signSymptoms.length > 0) {
            // Filter out empty values and capitalize each symptom
            const symptoms = event.signSymptoms
              .filter(s => s && (typeof s === 'string' ? s.trim() : true))
              .map(s => {
                if (typeof s === 'string') {
                  const trimmed = s.trim();
                  if (trimmed.toUpperCase() === 'N/A') return 'N/A';
                  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
                }
                return String(s);
              });
            const joined = cleanText(symptoms.join(', '));
            return joined || 'N/A';
          }
          return (event.signSymptoms && event.signSymptoms.trim && event.signSymptoms.trim() !== '') ? event.signSymptoms : 'N/A';
        })(),
        patientGender: (() => {
          const sex = event.patient?.otherDetail?.sex || event.patient?.sex;
          if (sex === 'M' || sex?.toLowerCase() === 'male') return 'Male';
          if (sex === 'F' || sex?.toLowerCase() === 'female') return 'Female';
          return sex || 'N/A';
        })(),
        patientAge: event.patient?.otherDetail?.age || event.patient?.age || 'N/A',
        typeOfHAE: (() => {
          const haeType = event.patient?.otherDetail?.typeOfHAE || event.patient?.typeOfHAE;
          return haeType ? toRomanNumeral(haeType) : 'N/A';
        })(),
        dateOfDiagnosis: event.patient?.otherDetail?.diagnosisDate || event.patient?.diagnosisDate || 'N/A',
        physicianName: event.patient?.otherDetail?.hcp1Name || event.patient?.hcp1Name || 'N/A',
        patientId: event.patient?._id || 'N/A',
        patientNumber: event.patient?.patientNumber ? `PAT0000${event.patient.patientNumber}` : 'N/A',
        patientName: event.patient?.otherDetail?.name || event.patient?.name || 'N/A',
        physicianEmail: event.patient?.otherDetail?.hcp1Email || event.patient?.hcp1Email || 'N/A',
        notes: event.notes || 'N/A'
      };
      });

      console.log('[TabularData] Transformed data sample:', transformedData[0]);
      console.log('[TabularData] medicationHAE type:', typeof transformedData[0]?.medicationHAE);

      setData(transformedData);
      setTotalRecords(result.total);
      setPagination(prev => ({ ...prev, total: result.total }));
    } catch (err) {
      console.error('[TabularData] Failed to load data:', err);
      setError(err.message);
      // Fallback to sample data if API fails
      // setData(sampleData);
    } finally {
      setLoading(false);
    }
  };

  // Export all filtered data to CSV (without pagination)
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);

      // Check for partial dates (same validation as loadTabularData)
      const hasPartialDates = (filters.startDate && !filters.endDate) || (!filters.startDate && filters.endDate);

      if (hasPartialDates) {
        alert('Please fill both Start Date and End Date, or leave both empty to export all data.');
        setExportLoading(false);
        return;
      }

      console.log('[TabularData] Exporting CSV with filters:', filters);

      // Fetch ALL filtered data (no pagination)
      const allData = await fetchAllTabularDataForCSV({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        hae: filters.haeType,
        gender: filters.gender,
        age: filters.ageRange,
        patientIds: filters.selectedPatients.length > 0 ? filters.selectedPatients : undefined
      });

      console.log('[TabularData] Fetched', allData.length, 'records for CSV export');

      if (allData.length === 0) {
        alert('No data to export with the current filters.');
        setExportLoading(false);
        return;
      }

      // Medications to exclude from CSV export
      const excludedMedications = ['cinryze'];

      // Transform data same way as displayed data
      const transformedData = allData.map((event) => {
        // Safely transform vials array to strings (filter out excluded medications like Cinryze)
        let medicationHAE = 'N/A';
        let medicationHAEDose = 'N/A';

        if (event.vials && Array.isArray(event.vials) && event.vials.length > 0) {
          try {
            // Filter out excluded medications (Cinryze)
            const filteredVials = event.vials.filter(v =>
              !excludedMedications.includes(v?.name?.toLowerCase())
            );
            if (filteredVials.length > 0) {
              medicationHAE = cleanText(filteredVials.map(v => v?.name || 'Unknown').join(', '));
              medicationHAEDose = cleanText(filteredVials.map(v => `${v?.dose || 0} ${(v?.doseType || '').toLowerCase()}`).join(', '));
            }
          } catch (err) {
            console.error('Error transforming vials:', err, event.vials);
          }
        }

        // Handle otherMedications - could be array or string
        let otherMedication = 'N/A';
        if (event.otherMedications) {
          if (Array.isArray(event.otherMedications) && event.otherMedications.length > 0) {
            const medications = event.otherMedications
              .filter(m => m && (m.name || (typeof m === 'string' && m.trim())))
              .map(m => {
                if (typeof m === 'string') {
                  return m.trim();
                }
                const name = m.name || 'Unknown';
                const dose = m.dose ? ` (${m.dose}${m.doseType ? ' ' + m.doseType : ''})` : '';
                return name + dose;
              });
            otherMedication = medications.length > 0 ? cleanText(medications.join(', ')) : 'N/A';
          } else if (typeof event.otherMedications === 'string' && event.otherMedications.trim()) {
            otherMedication = event.otherMedications.trim();
          }
        }

        return {
          id: event._id,
          eventDate: event.date || 'N/A',
          eventTime: event.time || 'N/A',
          eventType: toTitleCase(event.type) || 'N/A',
          attackDuration: event.duration ? (() => {
            // Convert seconds to "x Days xxh:xxm:xxs" format
            const days = Math.floor(event.duration / 86400);
            const hours = Math.floor((event.duration % 86400) / 3600);
            const minutes = Math.floor((event.duration % 3600) / 60);
            const seconds = event.duration % 60;

            const paddedHours = hours.toString().padStart(2, '0');
            const paddedMinutes = minutes.toString().padStart(2, '0');
            const paddedSeconds = seconds.toString().padStart(2, '0');

            return `${days} Days ${paddedHours}h:${paddedMinutes}m:${paddedSeconds}s`;
          })() : 'N/A',
          medicationHAE: toTitleCase(medicationHAE) || 'N/A',
          medicationHAEDose: medicationHAEDose || 'N/A',
          otherMedication: toTitleCase(otherMedication) || 'N/A',
          treatmentLocation: toTitleCase(event.treatmentLocation) || 'N/A',
          medicationInjectionSite: toTitleCase(cleanText(event.medication_injection_site)) || 'N/A',
          attackSeverity: (() => {
          const eventType = (event.type || '').toLowerCase();
          if (eventType === 'prodrome' || eventType === 'prophylaxis') return 'N/A';
          if (event.severity === null || event.severity === undefined) return 'N/A';
          return `${event.severity}/10`;
        })(),
          missedDays: (() => {
            // Default to 0 for attack events
            const eventType = (event.type || '').toLowerCase();
            if (eventType === 'attack') {
              return event.daysOffWork !== null && event.daysOffWork !== undefined ? event.daysOffWork : 0;
            }
            return event.daysOffWork !== null && event.daysOffWork !== undefined ? event.daysOffWork : 'N/A';
          })(),
          areaAffected: (() => {
            if (Array.isArray(event.areasAffected) && event.areasAffected.length > 0) {
              const joined = cleanText(event.areasAffected.filter(a => a && a.trim()).join(', '));
              return toTitleCase(joined) || 'N/A';
            }
            return (event.areasAffected && event.areasAffected.trim && event.areasAffected.trim() !== '') ? toTitleCase(cleanText(event.areasAffected)) : 'N/A';
          })(),
          triggers: (() => {
            if (Array.isArray(event.triggers) && event.triggers.length > 0) {
              const joined = cleanText(event.triggers.filter(t => t && t.trim()).join(', '));
              return toTitleCase(joined) || 'N/A';
            }
            return (event.triggers && event.triggers.trim && event.triggers.trim() !== '') ? toTitleCase(cleanText(event.triggers)) : 'N/A';
          })(),
          timeToTreatment: (event.timeBetween && parseFloat(event.timeBetween) > 0) ? (() => {
            const totalSeconds = parseFloat(event.timeBetween);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const paddedHours = hours.toString().padStart(2, '0');
            const paddedMinutes = minutes.toString().padStart(2, '0');
            const paddedSeconds = seconds.toString().padStart(2, '0');
            return `${paddedHours}h:${paddedMinutes}m:${paddedSeconds}s`;
          })() : 'N/A',
          signsAndSymptoms: (() => {
            if (Array.isArray(event.signSymptoms) && event.signSymptoms.length > 0) {
              const symptoms = event.signSymptoms
                .filter(s => s && (typeof s === 'string' ? s.trim() : true))
                .map(s => {
                  if (typeof s === 'string') {
                    const trimmed = s.trim();
                    if (trimmed.toUpperCase() === 'N/A') return 'N/A';
                    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
                  }
                  return s;
                });
              return cleanText(symptoms.join(', ')) || 'N/A';
            }
            return (event.signSymptoms && event.signSymptoms.trim && event.signSymptoms.trim() !== '') ? cleanText(event.signSymptoms) : 'N/A';
          })(),
          notes: event.notes || 'N/A',
          patientGender: (() => {
            const sex = event.patient?.otherDetail?.sex || event.patient?.sex;
            if (sex === 'M' || sex?.toLowerCase() === 'male') return 'Male';
            if (sex === 'F' || sex?.toLowerCase() === 'female') return 'Female';
            return sex || 'N/A';
          })(),
          patientAge: event.patient?.otherDetail?.age || event.patient?.age || 'N/A',
          typeOfHAE: (() => {
            const haeType = event.patient?.otherDetail?.typeOfHAE || event.patient?.typeOfHAE;
            return haeType ? toRomanNumeral(haeType) : 'N/A';
          })(),
          dateOfDiagnosis: event.patient?.otherDetail?.diagnosisDate || event.patient?.diagnosisDate || 'N/A',
          physicianName: event.patient?.otherDetail?.hcp1Name || event.patient?.hcp1Name || 'N/A',
          physicianEmail: event.patient?.otherDetail?.hcp1Email || event.patient?.hcp1Email || 'N/A',
          patientId: event.patient?._id || 'N/A',
          patientNumber: event.patient?.patientNumber ? `PAT0000${event.patient.patientNumber}` : 'N/A',
          patientName: event.patient?.otherDetail?.name || event.patient?.name || 'N/A'
        };
      });

      // Export to CSV
      exportToCSV(transformedData, 'patient_records.csv');

      console.log('[TabularData] CSV export completed successfully');
    } catch (err) {
      console.error('[TabularData] Export failed:', err);
      alert('Failed to export CSV: ' + err.message);
    } finally {
      setExportLoading(false);
    }
  };

  // Load patients list for dropdown filter
  useEffect(() => {
    const loadPatients = async () => {
      try {
        // Use 'all' parameter to fetch ALL patients without limit
        const result = await fetchPatients({ all: true });
        setPatients(result.data);
        console.log(`[TabularData] Loaded ${result.data.length} patients for dropdown`);
      } catch (err) {
        console.error('[TabularData] Failed to load patients:', err);
      }
    };
    loadPatients();
  }, []);

  // Load data on component mount and when filters or page changes
  useEffect(() => {
    loadTabularData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, filters.haeType, filters.gender, filters.ageRange, filters.selectedPatients, pagination.currentPage]);

  // Apply filters button handler
  const handleApplyFilters = () => {
    loadTabularData();
  };

  // Age range options - matching backend filters
  const ageRanges = [
    { label: 'All Ages', value: 'all' },
    { label: 'Infant (0-3)', value: 'infant' },
    { label: 'Child (3-15)', value: 'child' },
    { label: 'Adult (15+)', value: 'adult' }
  ];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset pagination to page 1 when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Use loaded patients for dropdown (already decrypted from API)
  const uniquePatients = patients.map(patient => ({
    id: patient._id,
    name: `${patient.otherDetail?.name || patient.name || 'Unknown'} (#${patient.patientNumber})`,
    patientNumber: patient.patientNumber
  }));

  const handlePatientSelection = (patientId) => {
    setFilters(prev => ({
      ...prev,
      selectedPatients: prev.selectedPatients.includes(patientId)
        ? prev.selectedPatients.filter(id => id !== patientId)
        : [...prev.selectedPatients, patientId]
    }));
    // Reset pagination to page 1 when patient filter changes
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: defaultDates.start,
      endDate: defaultDates.end,
      haeType: 'all',
      gender: 'all',
      ageRange: 'all',
      selectedPatients: []
    });
    // Reset pagination to page 1 when filters are reset
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Client-side search filter only (all other filters are server-side)
  const filteredData = data.filter(item => {
    // Search term filter - only search on string/number fields
    if (searchTerm.trim() === '') return true;

    return Object.values(item).some(value => {
      // Skip objects and arrays
      if (value === null || value === undefined) return false;
      if (typeof value === 'object') return false;

      return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
    });
  });

  // NOTE: Date, HAE, Gender, Age, and Patient filters are ALL handled by the backend API
  // Only search term is client-side

  // Check if client-side filters are active
  const hasClientSideFilters = searchTerm.trim() !== '';

  // Use filtered data count when client-side filters are active, otherwise use server total
  const displayTotal = hasClientSideFilters ? filteredData.length : pagination.total;
  const totalPages = hasClientSideFilters ? 1 : Math.ceil(pagination.total / pagination.pageSize);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Tabular Data</h1>
          <p className="text-neutral">
            {loading ? 'Loading...' : `Total Records: ${displayTotal}`}
          </p>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={exportLoading}
          className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="fas fa-file-export"></i>
          {exportLoading ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <i className="fas fa-exclamation-circle mr-2"></i>
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Search and Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex gap-4 items-center mb-4">
          <div className="flex-1 relative">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral"></i>
            <input
              type="text"
              placeholder="Search records..."
              className="w-full pl-10 pr-4 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Reset pagination to page 1 when search term changes
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
            />
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-light rounded-lg text-neutral hover:bg-light transition-colors duration-200"
          >
            <i className={`fas fa-filter mr-2 ${showFilters ? 'text-primary' : ''}`}></i>
            Filters
          </button>
        </div>

        {/* Expandable Filters Section */}
        {showFilters && (
          <div className="border-t border-light pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Date Range Filters */}
              <div className="relative z-50">
                <label className="block text-sm font-medium text-neutral mb-1">Start Date</label>
                <DatePicker
                  selected={filters.startDate ? new Date(filters.startDate) : null}
                  onChange={(date) => {
                    const formattedDate = date ? date.toISOString().split('T')[0] : '';
                    handleFilterChange({ target: { name: 'startDate', value: formattedDate } });
                  }}
                  dateFormat="dd/MM/yyyy"
                  maxDate={filters.endDate ? new Date(filters.endDate) : null}
                  className="w-full p-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholderText="Select start date"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  todayButton="Today"
                  popperClassName="z-50"
                />
              </div>
              <div className="relative z-50">
                <label className="block text-sm font-medium text-neutral mb-1">End Date</label>
                <DatePicker
                  selected={filters.endDate ? new Date(filters.endDate) : null}
                  onChange={(date) => {
                    const formattedDate = date ? date.toISOString().split('T')[0] : '';
                    handleFilterChange({ target: { name: 'endDate', value: formattedDate } });
                  }}
                  dateFormat="dd/MM/yyyy"
                  minDate={filters.startDate ? new Date(filters.startDate) : null}
                  className="w-full p-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholderText="Select end date"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  todayButton="Today"
                  popperClassName="z-50"
                />
              </div>

              {/* HAE Type Filter */}
              <div>
                <label className="block text-sm font-medium text-neutral mb-1">Type of HAE</label>
                <select
                  name="haeType"
                  value={filters.haeType}
                  onChange={handleFilterChange}
                  className="w-full p-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="1">Type I</option>
                  <option value="2">Type II</option>
                  <option value="3">Type III</option>
                </select>
              </div>

              {/* Gender Filter */}
              <div>
                <label className="block text-sm font-medium text-neutral mb-1">Gender</label>
                <select
                  name="gender"
                  value={filters.gender}
                  onChange={handleFilterChange}
                  className="w-full p-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Genders</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              {/* Age Range Filter */}
              <div>
                <label className="block text-sm font-medium text-neutral mb-1">Age Range</label>
                <select
                  name="ageRange"
                  value={filters.ageRange}
                  onChange={handleFilterChange}
                  className="w-full p-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {ageRanges.map(range => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Patient Filter - Replace the existing select with custom dropdown */}
              <div className="relative z-50">
                <label className="block text-sm font-medium text-neutral mb-1">Patients</label>
                <button
                  type="button"
                  onClick={() => setShowPatientDropdown(!showPatientDropdown)}
                  className="w-full p-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-left flex justify-between items-center bg-white"
                >
                  <span>
                    {filters.selectedPatients.length === 0
                      ? 'Select Patients'
                      : `${filters.selectedPatients.length} Patient${filters.selectedPatients.length === 1 ? '' : 's'} Selected`}
                  </span>
                  <i className={`fas fa-chevron-${showPatientDropdown ? 'up' : 'down'} text-neutral`}></i>
                </button>

                {showPatientDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-light rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {uniquePatients.map(patient => (
                      <div
                        key={patient.id}
                        className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                        onClick={() => handlePatientSelection(patient.id)}
                      >
                        <input
                          type="checkbox"
                          checked={filters.selectedPatients.includes(patient.id)}
                          onChange={() => {}}
                          className="h-4 w-4 text-primary border-light rounded focus:ring-primary"
                        />
                        <span className="ml-3 text-sm text-neutral">{patient.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Reset Filters Button */}
            <div className="flex justify-end mt-4">
              <button
                onClick={resetFilters}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div className="bg-white p-8 rounded-lg shadow mb-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <span className="ml-4 text-neutral font-medium">Loading tabular data...</span>
          </div>
        </div>
      )}

      {/* Message when only one date is selected */}
      {!loading && ((filters.startDate && !filters.endDate) || (!filters.startDate && filters.endDate)) && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-6 py-4 rounded-lg mb-6 flex items-center">
          <i className="fas fa-exclamation-triangle mr-3 text-xl"></i>
          <span>Please select both Start Date and End Date to filter data, or leave both empty to see all records.</span>
        </div>
      )}

      {/* Table */}
      {!loading && !((filters.startDate && !filters.endDate) || (!filters.startDate && filters.endDate)) && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-320px)] md:max-h-[calc(100vh-280px)] lg:max-h-[calc(100vh-250px)]">
            <table className="w-full min-w-max">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Event Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Event Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Attack Duration</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Medication (HAE)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Medication (HAE Dose)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Other Medication</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Treatment Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Medication Injection Site</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Attack Severity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Days of Work / School Missed</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Area Affected</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Triggers</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Time from Attack to Treatment</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Signs & Symptoms</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Notes</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Patient Gender</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Patient Age</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Type of HAE</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Date of Diagnosis</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Physician Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Patient ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Physician Email</th>
              {/* <th className="px-6 py-3 text-left text-xs font-medium text-neutral tracking-wider">Actions</th> */}
            </tr>
          </thead>
          <tbody className="divide-y divide-light">
            {filteredData.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.eventDate}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.eventType}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.attackDuration}</td>
                <td className="px-6 py-4 text-sm text-primary max-w-xs group relative">
                  <div className="truncate">{item.medicationHAE}</div>
                  {item.medicationHAE && item.medicationHAE.length > 30 && (
                    <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 whitespace-normal break-words shadow-lg max-w-md">
                      {item.medicationHAE}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-primary max-w-xs group relative">
                  <div className="truncate">{item.medicationHAEDose}</div>
                  {item.medicationHAEDose && item.medicationHAEDose.length > 30 && (
                    <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 whitespace-normal break-words shadow-lg max-w-md">
                      {item.medicationHAEDose}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-primary max-w-xs group relative">
                  <div className="truncate">{item.otherMedication}</div>
                  {item.otherMedication && item.otherMedication.length > 30 && (
                    <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 whitespace-normal break-words shadow-lg max-w-md">
                      {item.otherMedication}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.treatmentLocation}</td>
                <td className="px-6 py-4 text-sm text-primary max-w-xs group relative">
                  <div className="truncate">{item.medicationInjectionSite}</div>
                  {item.medicationInjectionSite && item.medicationInjectionSite.length > 30 && (
                    <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 whitespace-normal break-words shadow-lg max-w-md">
                      {item.medicationInjectionSite}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 inline-flex leading-5 rounded-full ${
                    (() => {
                      const severityNum = parseInt(item.attackSeverity) || 0;
                      if (severityNum >= 8) return 'font-semibold text-xs  bg-red-100 text-red-800';
                      if (severityNum >= 4) return 'font-semibold text-xs  bg-yellow-100 text-yellow-800';
                      if (severityNum == 0) return 'text-primary ';
                      return 'font-semibold bg-green-100 text-green-800';
                    })()
                  }`}>
                    {item.attackSeverity}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.missedDays}</td>
                <td className="px-6 py-4 text-sm text-primary max-w-xs group relative">
                  <div className="truncate">{item.areaAffected || "N/A"}</div>
                  {item.areaAffected && item.areaAffected.length > 30 && (
                    <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 whitespace-normal break-words shadow-lg max-w-md">
                      {item.areaAffected}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-primary max-w-xs group relative">
                  <div className="truncate">{item.triggers || "N/A"}</div>
                  {item.triggers && item.triggers.length > 30 && (
                    <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 whitespace-normal break-words shadow-lg max-w-md">
                      {item.triggers}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.timeToTreatment}</td>
                <td className="px-6 py-4 text-sm text-primary max-w-xs group relative">
                  <div className="truncate">{item.signsAndSymptoms || "N/A"}</div>
                  {item.signsAndSymptoms && item.signsAndSymptoms.length > 30 && (
                    <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 whitespace-normal break-words shadow-lg max-w-md">
                      {item.signsAndSymptoms}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-primary max-w-xs group relative">
                  <div className="truncate">{item.notes}</div>
                  {item.notes && item.notes.length > 30 && (
                    <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 whitespace-normal break-words shadow-lg max-w-md">
                      {item.notes}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.patientGender}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.patientAge}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.typeOfHAE}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.dateOfDiagnosis}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.physicianName}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{item.patientNumber}</td>
                <td className="px-6 py-4 text-sm text-primary max-w-xs group relative">
                  <div className="truncate">{item.physicianEmail}</div>
                  {item.physicianEmail && item.physicianEmail.length > 30 && (
                    <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 left-0 top-full mt-1 whitespace-normal break-words shadow-lg max-w-md">
                      {item.physicianEmail}
                    </div>
                  )}
                </td>
                {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button className="text-primary hover:text-primary-dark mr-3">
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="text-red-600 hover:text-red-900">
                    <i className="fas fa-trash"></i>
                  </button>
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>
          </div>
        </div>
      )}

      {/* Pagination - Only show when there are results */}
      {displayTotal > 0 ? (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{hasClientSideFilters ? 1 : (pagination.currentPage - 1) * pagination.pageSize + 1}</span> to{' '}
                  <span className="font-medium">
                    {hasClientSideFilters ? displayTotal : Math.min(pagination.currentPage * pagination.pageSize, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{displayTotal}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <i className="fas fa-chevron-left"></i>
                  </button>

                  {/* Page numbers */}
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= pagination.currentPage - 1 && pageNumber <= pagination.currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNumber === pagination.currentPage
                              ? 'z-10 bg-primary border-primary text-white'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === pagination.currentPage - 2 ||
                      pageNumber === pagination.currentPage + 2
                    ) {
                      return (
                        <span
                          key={pageNumber}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}

                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </nav>
              </div>
            </div>
        </div>
      ) : (
        <div className="bg-white px-4 py-6 text-center border-t border-gray-200 mt-4 rounded-lg shadow">
          <p className="text-gray-500 text-sm">No records found</p>
        </div>
      )}
    </div>
  );
};

export default TabularData; 